import type { Route } from './+types/service-areas';
import { AREAS, NEARBY_CITIES } from '~/data/areas';
import { Link } from 'react-router';
import { RelatedPages } from '~/components/related-pages';
import { buildMeta, schemaFor } from '~/lib/seo';
import { SITE } from '~/data/site';
import { brandingFromMatches } from '~/lib/branding';

// Both lists come from the ONE source — src/app/data/areas.ts. These were two
// more hand-maintained copies of the same six neighborhoods.
const ST_PETE_HOODS = AREAS.map((a) => ({
  slug: a.slug,
  name: a.name,
  desc: a.blurb,
  photo: a.photo,
  photoAlt: a.photoAlt,
}));

const TAMPA_BAY_CITIES = NEARBY_CITIES;

// NOTE: only the meta identity is fixed here. The page body below (St. Pete
// neighborhood cards, the Tampa Bay city list) is genuinely New Leaf-specific
// geography, not a swappable brand string — there is no per-tenant
// service-area data model yet, so it is left as-is rather than half-branded.
export const meta: Route.MetaFunction = ({ matches }) => {
  const branding = brandingFromMatches(matches);
  return buildMeta({
    title: 'Service Areas',
    description: branding.isNewLeaf
      ? 'New Leaf Cleaning serves Greater Tampa Bay — St. Petersburg, Tampa, Clearwater, and surrounding cities. Same crew, same standard.'
      : `${branding.name} service areas.`,
    path: '/service-areas',
    siteName: branding.name,
    jsonLd: schemaFor('/service-areas'),
  });
};

export default function ServiceAreasPage() {
  return (
    <main>
      <section className="section">
        <div className="container">
          <p className="eyebrow page-eyebrow">Where we clean</p>
          <h1 className="display-lg page-h1">
            We serve the{' '}
            <em className="italic-display">Greater Tampa Bay area.</em>
          </h1>
          <p className="lede chapter-lede">
            We started in St. Petersburg because this is where we live. We&rsquo;ve grown
            across Tampa Bay &mdash; and we&rsquo;ll keep growing wherever we can keep
            the standard.
          </p>
        </div>
      </section>

      {/* ── ST. PETE CORE ────────────────────────────────── */}
      <section className="section-sm bg-bone-warm">
        <div className="container">
          <h2 className="display-md">Core St. Pete neighborhoods</h2>
          <div className="area-grid">
            {ST_PETE_HOODS.map((hood) => (
              <Link key={hood.slug} to={`/areas/${hood.slug}`} className="area-card reveal">
                <img
                  src={hood.photo}
                  alt=""
                  className="area-card-photo"
                  aria-hidden="true"
                  loading="lazy"
                />
                <div className="area-card-name">{hood.name}</div>
                <p className="area-card-desc">{hood.desc}</p>
                <span className="hand hand-explore">Explore →</span>
              </Link>
            ))}
          </div>
          <p className="areas-also-serve">
            We also serve Gulfport, Pinellas Point, Euclid–St. Paul, Downtown St. Pete,
            Coquina Key, and the Historic Grand Central District.
          </p>
        </div>
      </section>

      {/* ── GREATER TAMPA BAY ────────────────────────────── */}
      <section className="section-sm">
        <div className="container">
          <h2 className="display-md">Greater Tampa Bay</h2>
          <p className="lede chapter-lede">Same crew, same standard &mdash; wherever we go.</p>
          <div className="bay-grid">
            {TAMPA_BAY_CITIES.map((city) => (
              <div className="bay-card reveal" key={city}>
                <div className="bay-card-name">{city}</div>
              </div>
            ))}
          </div>
          <p className="marginalia chapter-marginalia">
            Not on this list? Ask us &mdash; if we can get to you, we will.
          </p>
        </div>
      </section>

      <RelatedPages path="/service-areas" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Your area</p>
          <h2>See your <em>exact price</em>.</h2>
          <p className="cta-lede">Under a minute. No call required.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">See your price →</Link>
        </div>
      </section>
    </main>
  );
}
